import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Moon, Sun, Home, MessageSquare, Newspaper, HelpCircle, Mail, Search, ChevronRight } from 'lucide-react';
import { SoundWaveAvatar } from './SoundWaveAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTheme } from '@/context/ThemeContext';
import { useChat } from '../../hooks/useChat';
import { useConfig } from '../../hooks/useConfig';
import { API } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';

const HELP_ITEMS = [
  { q: 'What is Whisper.AI?', a: 'Whisper.AI is an AI-powered chat widget platform. Deploy custom chatbots on any website in 60 seconds.' },
  { q: 'How do I add the bot to my website?', a: 'Copy the one-line embed code from your dashboard and paste it before </body> on your website. Visit whisperai.one/guide for platform-specific instructions.' },
  { q: 'What are the pricing plans?', a: 'Free ($0, 15 msgs), Single ($10, 500 msgs), Starter ($15, 2K msgs), Pro ($39, 10K msgs), Business ($99, 50K msgs). All include Knowledge Base and accent color.' },
  { q: 'How does the Knowledge Base work?', a: 'Add Q&A pairs in your dashboard. The bot uses them to answer visitor questions with your specific information.' },
  { q: 'Can I customize the widget?', a: 'Yes! Change bot name, accent color, theme (dark/light), welcome message, quick questions, and more from your dashboard.' },
  { q: 'What is Website Crawl?', a: 'Enter your URL and the bot auto-learns from your site pages. Available from the Single plan ($10/mo).' },
];

function formatBotMessage(text) {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return part.split('\n').map((line, j, arr) => (
      <span key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</span>
    ));
  });
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('home');
  const [bouncing, setBouncing] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [helpSearch, setHelpSearch] = useState('');
  const [expandedHelp, setExpandedHelp] = useState(null);
  const [showHelpForm, setShowHelpForm] = useState(false);
  const [helpEmail, setHelpEmail] = useState('');
  const [helpName, setHelpName] = useState('');
  const [helpMsg, setHelpMsg] = useState('');
  const [helpSending, setHelpSending] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  const [input, setInput] = useState('');

  const { config, getFullSystemPrompt } = useConfig();
  const { messages, isLoading, sendMessage, clearMessages } = useChat(getFullSystemPrompt);
  const { theme, toggleTheme } = useTheme();
  const hasAutoOpened = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load blog posts for News tab
  useEffect(() => {
    axios.get(`${API}/blog?page=1&limit=5`).then(r => setBlogPosts(r.data.posts || [])).catch(() => {});
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    if (tab === 'messages') messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, tab]);

  // Focus input when switching to messages
  useEffect(() => {
    if (isOpen && tab === 'messages') setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, tab]);

  // Greeting + auto-open
  useEffect(() => {
    const greetTimer = setTimeout(() => { if (!isOpen) setShowGreeting(true); }, 2000);
    const openTimer = setTimeout(() => {
      if (!hasAutoOpened.current) {
        hasAutoOpened.current = true;
        setShowGreeting(false);
        setBouncing(true);
        if (!sessionStorage.getItem('whisper_demo_sound')) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const buf = ctx.createBuffer(1, ctx.sampleRate * 0.35, ctx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < data.length; i++) {
              const t = i / ctx.sampleRate;
              const env = Math.max(0, 1 - t * 3);
              data[i] = (Math.sin(2 * Math.PI * 880 * t) * env + Math.sin(2 * Math.PI * 1320 * t) * env * 0.5) * 0.15;
            }
            const src = ctx.createBufferSource();
            src.buffer = buf;
            src.connect(ctx.destination);
            src.start();
            sessionStorage.setItem('whisper_demo_sound', '1');
          } catch (e) { /* Audio blocked */ }
        }
        setTimeout(() => { setIsOpen(true); setBouncing(false); }, 800);
      }
    }, 5000);
    return () => { clearTimeout(greetTimer); clearTimeout(openTimer); };
  }, [isOpen]);

  const handleSend = (text) => {
    const msg = text || input.trim();
    if (!msg || isLoading) return;
    setInput('');
    sendMessage(msg);
  };

  const handleQuickQuestion = (q) => {
    setTab('messages');
    sendMessage(q);
  };

  const filteredHelp = HELP_ITEMS.filter(h =>
    h.q.toLowerCase().includes(helpSearch.toLowerCase()) || h.a.toLowerCase().includes(helpSearch.toLowerCase())
  );

  const TABS = [
    { id: 'home', icon: Home, label: 'Home' },
    { id: 'messages', icon: MessageSquare, label: 'Messages' },
    { id: 'news', icon: Newspaper, label: 'News' },
    { id: 'help', icon: HelpCircle, label: 'Help' },
  ];

  if (!isOpen) {
    return (
      <>
        {showGreeting && (
          <div className="fixed bottom-[88px] right-4 sm:right-6 z-50 cursor-pointer" style={{ animation: 'slideUp 0.3s ease' }}>
            <div onClick={() => { setShowGreeting(false); setIsOpen(true); }} className="bg-card border border-border rounded-2xl rounded-br-sm shadow-[0_4px_20px_rgba(0,0,0,0.12)] px-4 py-3 text-sm font-medium text-foreground hover:scale-[1.02] transition-transform relative">
              Hi! Need help?
              <button onClick={(e) => { e.stopPropagation(); setShowGreeting(false); }} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground text-[10px]">
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
        <button onClick={() => { setShowGreeting(false); setIsOpen(true); }} className={`fixed bottom-6 right-4 sm:right-6 w-14 h-14 rounded-full shadow-[0_4px_24px_rgb(0,0,0,0.2)] z-50 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95 bg-[#18181B] text-white ${bouncing ? 'animate-bounce' : ''}`} data-testid="chat-launcher-bubble">
          <SoundWaveAvatar isAnimating={false} size={32} />
        </button>
      </>
    );
  }

  return (
    <>
      <div className="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[80vh] rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden border border-border/60 z-50 bg-background" style={{ animation: 'slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }} data-testid="chat-window">

        {/* Header */}
        <div className="px-5 py-4 shrink-0" style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <SoundWaveAvatar isAnimating={isLoading} size={24} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">{config.botName}</h3>
                <span className="text-xs text-white/70 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                  Online now
                </span>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10">
                {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => { setIsOpen(false); clearMessages(); }} className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0" data-testid="chat-content">

          {/* Home Tab */}
          {tab === 'home' && (
            <div className="p-5">
              <p className="text-xl font-bold text-foreground mb-1">Hi there! 👋</p>
              <p className="text-sm text-muted-foreground mb-5">{config.botName} — ask me anything about Whisper.AI</p>
              <div className="space-y-1.5">
                {['What is Whisper.AI?', 'How do I add the chat bot to my website?', 'What are your pricing plans?', 'How does the Knowledge Base work?', 'Can I try it for free?'].map((q) => (
                  <button key={q} onClick={() => handleQuickQuestion(q)} className="w-full text-left px-3.5 py-2.5 rounded-xl border border-border/60 text-xs text-foreground hover:border-[#10B981]/40 hover:bg-[#10B981]/5 transition-all flex items-center gap-2 group">
                    <span className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 group-hover:bg-[#10B981]/20"><Send className="h-2.5 w-2.5 text-[#10B981]" /></span>
                    {q}
                  </button>
                ))}
              </div>
              {messages.length > 0 && (
                <button onClick={() => setTab('messages')} className="mt-4 text-xs text-[#10B981] hover:underline flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" /> Continue conversation ({messages.length} messages)
                </button>
              )}
            </div>
          )}

          {/* Messages Tab */}
          {tab === 'messages' && (
            <>
              <div className="flex-1 px-4 py-4 space-y-1">
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                    <SoundWaveAvatar isAnimating={false} size={36} />
                    <p className="text-xs text-muted-foreground mt-3">Send a message to start chatting</p>
                  </div>
                )}
                {messages.map((msg, i) => {
                  const isUser = msg.role === 'user';
                  const prevMsg = messages[i - 1];
                  const sameSender = prevMsg && prevMsg.role === msg.role;
                  return (
                    <div key={i} className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'} ${sameSender ? 'mt-0.5' : 'mt-3'}`} style={{ animation: 'msgFadeIn 0.3s ease forwards', animationDelay: `${Math.min(i * 0.05, 0.3)}s`, opacity: 0 }}>
                      {!isUser && !sameSender && (
                        <div className="w-7 h-7 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0">
                          <SoundWaveAvatar isAnimating={false} size={18} />
                        </div>
                      )}
                      {!isUser && sameSender && <div className="w-7" />}
                      <div className={`max-w-[75%] px-3.5 py-2.5 text-[13px] leading-relaxed break-words ${isUser ? 'bg-[#10B981] text-white rounded-2xl rounded-br-md' : 'bg-card text-card-foreground border border-border/60 rounded-2xl rounded-bl-md'}`} style={{ overflowWrap: 'anywhere' }}>
                        {isUser ? msg.content : formatBotMessage(msg.content)}
                      </div>
                    </div>
                  );
                })}
                {isLoading && (
                  <div className="flex gap-2 justify-start mt-3">
                    <div className="w-7 h-7 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0">
                      <SoundWaveAvatar isAnimating={true} size={18} />
                    </div>
                    <div className="bg-card border border-border/60 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" style={{ animationDelay: '0s' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" style={{ animationDelay: '0.15s' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" style={{ animationDelay: '0.3s' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Talk to Human */}
              {messages.length >= 5 && !helpSent && !showHelpForm && (
                <div className="px-3 py-1.5 shrink-0">
                  <button onClick={() => setShowHelpForm(true)} className="w-full py-2 rounded-lg border border-border/60 text-xs text-muted-foreground hover:text-foreground hover:border-[#10B981]/40 transition-all flex items-center justify-center gap-1.5" data-testid="talk-to-human-btn">
                    <Mail className="h-3 w-3" /> Talk to a Human
                  </button>
                </div>
              )}
              {showHelpForm && !helpSent && (
                <div className="px-3 py-2 space-y-1.5 border-t border-border/50 shrink-0">
                  <Input value={helpName} onChange={(e) => setHelpName(e.target.value)} placeholder="Your name (optional)" className="h-7 text-xs" />
                  <Input type="email" value={helpEmail} onChange={(e) => setHelpEmail(e.target.value)} placeholder="Your email *" className="h-7 text-xs" />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 h-7 text-xs bg-[#10B981] hover:bg-[#059669]" disabled={!helpEmail.trim() || helpSending} onClick={async () => {
                      setHelpSending(true);
                      try {
                        await axios.post(`${API}/embed/demo/human-help`, { widget_id: 'demo', visitor_name: helpName, visitor_email: helpEmail, message: helpMsg || 'Requested help', conversation: messages.map(m => ({ role: m.role, content: m.content })) });
                        setHelpSent(true); toast.success('Request sent!');
                      } catch { toast.error('Failed'); }
                      finally { setHelpSending(false); }
                    }}>{helpSending ? 'Sending...' : 'Send'}</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowHelpForm(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              {helpSent && <div className="px-3 py-2 text-center shrink-0"><p className="text-xs text-[#10B981]">{"✓ Request sent!"}</p></div>}

              {/* Input */}
              <div className="p-3 border-t border-border/50 shrink-0">
                <div className="flex items-center gap-2 bg-card border border-border/60 rounded-xl px-3 py-1 focus-within:ring-2 focus-within:ring-[#10B981]/20 focus-within:border-[#10B981]/40 transition-all">
                  <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Type a message..." className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 outline-none py-2.5" disabled={isLoading} data-testid="chat-input" />
                  <button type="button" onClick={() => handleSend()} disabled={!input.trim() || isLoading} className="w-8 h-8 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white shrink-0 disabled:opacity-20 flex items-center justify-center transition-all" data-testid="send-message-btn">
                    <Send className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* News Tab */}
          {tab === 'news' && (
            <div>
              {blogPosts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <Newspaper className="h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-xs text-muted-foreground">No announcements yet</p>
                </div>
              ) : blogPosts.map((post) => (
                <a key={post.post_id || post.slug} href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="block p-4 border-b border-border/50 hover:bg-muted/30 transition-colors">
                  {post.cover_image && <img src={post.cover_image} alt="" className="w-full h-28 object-cover rounded-lg mb-2" />}
                  <h4 className="text-sm font-semibold text-foreground mb-1">{post.title}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">{post.excerpt || post.content?.slice(0, 100)}</p>
                </a>
              ))}
            </div>
          )}

          {/* Help Tab */}
          {tab === 'help' && (
            <div>
              <div className="p-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input value={helpSearch} onChange={(e) => setHelpSearch(e.target.value)} placeholder="Search help..." className="w-full pl-9 pr-3 py-2.5 border border-border/60 rounded-xl text-xs bg-transparent text-foreground outline-none focus:ring-1 focus:ring-[#10B981]/30" />
                </div>
              </div>
              {filteredHelp.map((h, i) => (
                <div key={i} className="border-b border-border/50">
                  <button onClick={() => setExpandedHelp(expandedHelp === i ? null : i)} className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                    <span className="text-sm font-medium text-foreground pr-4">{h.q}</span>
                    <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${expandedHelp === i ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedHelp === i && (
                    <div className="px-4 pb-3">
                      <p className="text-xs text-muted-foreground leading-relaxed">{h.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tab Bar */}
        <div className="flex border-t border-border/50 shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-2.5 flex flex-col items-center gap-1 text-[10px] transition-colors ${tab === t.id ? 'text-[#10B981]' : 'text-muted-foreground'}`} data-testid={`tab-${t.id}`}>
              <t.icon className="h-[18px] w-[18px]" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center py-1.5 shrink-0">
          <p className="text-[10px] text-muted-foreground/50">
            Powered by <a href="https://whisperai.one/" target="_blank" rel="noopener noreferrer" className="hover:text-muted-foreground transition-colors">Whisper.AI</a>
          </p>
        </div>
      </div>

      {/* Bubble */}
      <button onClick={() => setIsOpen(false)} className="fixed bottom-6 right-4 sm:right-6 w-14 h-14 rounded-full shadow-[0_4px_24px_rgb(0,0,0,0.2)] z-50 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95 bg-[#18181B] text-white" data-testid="chat-launcher-bubble">
        <X className="h-6 w-6" />
      </button>
    </>
  );
}
